/**
 * Query helpers shared by every sample-data resolver: search, sort and paging
 * applied locally so a tour's tables, dropdowns and search boxes behave the
 * way the live ones do — without a request.
 */

/** The list envelope every paged list endpoint in the app returns. */
export interface TourListPage<T> {
  items: T[];
  totalCount: number;
  offset: number;
  limit: number;
  totalPages: number;
}

/** Values worth matching a free-text search against, in preference order. */
const SEARCHABLE_KEY_HINTS = [
  'name',
  'label',
  'code',
  'title',
  'description',
  'id',
  'no',
  'email',
  'status',
];

const isSearchableKey = (key: string): boolean => {
  const lower = key.toLowerCase();
  return SEARCHABLE_KEY_HINTS.some(hint => lower.includes(hint));
};

const matchesQuery = (value: unknown, query: string): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.toLowerCase().includes(query);
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).toLowerCase().includes(query);
  }
  return false;
};

/**
 * Free-text search over sample records, mirroring the server's "search across
 * the visible columns" behaviour. Matching is case-insensitive and substring
 * based, so typing "Arun" finds "Arun Kumar" and "Performance" finds
 * "Annual Performance Award" — the tour follows what the user actually typed
 * instead of jumping to a hard-coded record.
 *
 * `keys` narrows the match to specific fields; without it the search looks at
 * every scalar field whose name reads like a label/code/identifier, which is
 * what the list endpoints search on.
 */
export const searchTourData = <T extends object>(
  items: readonly T[],
  searchValue: string | null | undefined,
  keys?: (keyof T)[]
): T[] => {
  const query = (searchValue ?? '').trim().toLowerCase();
  if (!query) return [...items];

  return items.filter(item => {
    const entries = keys
      ? keys.map(key => item[key])
      : Object.entries(item)
          .filter(([key]) => isSearchableKey(key))
          .map(([, value]) => value);
    return entries.some(value => matchesQuery(value, query));
  });
};

/** Case-insensitive, type-aware compare used by the local sort. */
const compareValues = (a: unknown, b: unknown): number => {
  if (a === b) return 0;
  if (a === null || a === undefined) return -1;
  if (b === null || b === undefined) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;

  const toStringVal = (val: unknown): string => {
    if (typeof val === 'string') return val;
    if (typeof val === 'number' || typeof val === 'boolean') return String(val);
    return JSON.stringify(val);
  };

  const left = toStringVal(a);
  const right = toStringVal(b);
  const leftDate = Date.parse(left);
  const rightDate = Date.parse(right);
  if (!Number.isNaN(leftDate) && !Number.isNaN(rightDate)) {
    return leftDate - rightDate;
  }
  return left.localeCompare(right, undefined, { sensitivity: 'base' });
};

/** Sort sample rows by a column, matching the tables' `sortBy`/`sortDir`. */
export const sortTourData = <T extends object>(
  items: readonly T[],
  sortBy?: string | null,
  sortDir?: string | null
): T[] => {
  if (!sortBy) return [...items];
  const direction = String(sortDir ?? 'asc').toLowerCase() === 'desc' ? -1 : 1;
  const read = (row: T) => (row as Record<string, unknown>)[sortBy];
  return [...items].sort((a, b) => compareValues(read(a), read(b)) * direction);
};

/**
 * Page a sample list the way the backend does.
 *
 * `offset` is a **page index**, not a row index — the backend treats it as the
 * page number, and every caller in the app passes the page through unchanged.
 * Slicing by `offset * limit` here would silently skip rows and leave a tour's
 * table empty on page 2.
 */
export const paginateTourData = <T>(
  items: readonly T[],
  offset: number | null | undefined,
  limit: number | null | undefined
): TourListPage<T> => {
  const safeLimit = Number(limit) > 0 ? Number(limit) : items.length || 10;
  const page = Math.max(0, Number(offset) || 0);
  const start = page * safeLimit;

  return {
    items: items.slice(start, start + safeLimit),
    totalCount: items.length,
    offset: page,
    limit: safeLimit,
    totalPages: Math.max(1, Math.ceil(items.length / safeLimit)),
  };
};

/**
 * Search → sort → page, the order every list endpoint applies them in.
 * Reads the standard query parameter names, accepting the casing variants the
 * services use (`Offset`/`offset`, `SearchField`/`searchField`, …).
 */
export const listTourData = <T extends object>(
  items: readonly T[],
  query: URLSearchParams,
  options: { searchKeys?: (keyof T)[] } = {}
): TourListPage<T> => {
  const read = (...names: string[]): string | null => {
    for (const name of names) {
      const value = query.get(name);
      if (value !== null) return value;
    }
    return null;
  };

  const searched = searchTourData(
    items,
    read('SearchField', 'searchField', 'Search', 'search'),
    options.searchKeys
  );
  const sorted = sortTourData(
    searched,
    read('SortBy', 'sortBy'),
    read('SortDir', 'sortDir', 'sortDirection')
  );

  // An export asks for every row at once — no paging applied.
  if (read('isExport', 'IsExport') === 'true') {
    return {
      items: sorted,
      totalCount: sorted.length,
      offset: 0,
      limit: sorted.length,
      totalPages: 1,
    };
  }

  return paginateTourData(
    sorted,
    Number(read('Offset', 'offset') ?? 0),
    Number(read('Limit', 'limit') ?? 10)
  );
};
